import { STORE_COLLECTION_KINDS, type StoreCollectionKind } from '@mediardex/contracts'
import { useEffect, useState } from 'react'
import { MdClose } from 'react-icons/md'
import { CategoryChecklist } from './collection-settings'

interface NewCollectionDialogProps {
    open: boolean
    busy: boolean
    categories: { id: number; name: string }[]
    onClose: () => void
    onCreate: (data: { title: string; kind: StoreCollectionKind; categoryIds: number[] }) => void
}

/** "Nova coleção": título e como os produtos entram. O resto se ajusta depois. */
export default function NewCollectionDialog({ open, busy, categories, onClose, onCreate }: NewCollectionDialogProps) {
    const [title, setTitle] = useState('')
    const [kind, setKind] = useState<StoreCollectionKind>('MANUAL')
    const [categoryIds, setCategoryIds] = useState<number[]>([])

    useEffect(() => {
        if (!open) return
        setTitle('')
        setKind('MANUAL')
        setCategoryIds([])
    }, [open])

    if (!open) return null

    const canCreate = title.trim() && (kind !== 'CATEGORY' || categoryIds.length > 0)

    return (
        <dialog className="modal modal-open z-50">
            <div className="modal-box !mx-4 !max-w-2xl !p-0">
                <div className="flex items-center justify-between border-b border-base-200 p-4">
                    <h2 className="text-lg font-bold">Nova coleção</h2>
                    <button type="button" className="btn btn-ghost btn-sm" onClick={onClose}>
                        <MdClose className="text-lg" />
                    </button>
                </div>
                <div className="flex max-h-[70vh] flex-col gap-5 overflow-y-auto p-4">
                    <div className="flex flex-col gap-1.5">
                        <span className="text-sm font-medium">Título</span>
                        <input
                            type="text"
                            className="input input-bordered w-full"
                            maxLength={120}
                            placeholder="Ex.: Coleção de verão, Lançamentos, Presentes"
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                            autoFocus
                        />
                        <span className="text-xs text-base-content/60">
                            O endereço sai do título (/collection/colecao-de-verao) e pode ser trocado depois.
                        </span>
                    </div>

                    <div className="flex flex-col gap-2">
                        <span className="text-sm font-medium">Produtos</span>
                        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                            {STORE_COLLECTION_KINDS.map((option) => (
                                <button
                                    type="button"
                                    key={option.value}
                                    onClick={() => setKind(option.value)}
                                    className={`flex flex-col gap-1 rounded-xl border p-3 text-left transition-colors ${kind === option.value ? 'border-primary bg-primary/5' : 'border-base-300 hover:border-primary'}`}
                                >
                                    <span className="font-semibold">{option.label}</span>
                                    <span className="text-xs text-base-content/70">{option.help}</span>
                                </button>
                            ))}
                        </div>
                    </div>

                    {kind === 'CATEGORY' && (
                        <div className="flex flex-col gap-2">
                            <span className="text-sm font-medium">Categorias</span>
                            <CategoryChecklist categories={categories} value={categoryIds} onChange={setCategoryIds} />
                        </div>
                    )}

                    <div className="flex justify-end gap-2">
                        <button type="button" className="btn btn-sm btn-ghost" onClick={onClose}>
                            Cancelar
                        </button>
                        <button
                            type="button"
                            className="btn btn-sm btn-primary"
                            disabled={busy || !canCreate}
                            onClick={() => onCreate({ title: title.trim(), kind, categoryIds })}
                        >
                            {busy && <span className="loading loading-spinner loading-xs" />} Criar coleção
                        </button>
                    </div>
                </div>
            </div>
            <div className="modal-backdrop" onClick={onClose} />
        </dialog>
    )
}
